/**
 * LoginPage — Aile Chat sign-in surface.
 *
 * B2B SaaS production-grade: Magic Link + OAuth (Google / Microsoft) + SAML SSO
 * stub, with proper a11y (WCAG 2.2 AA), i18n (ja-JP / en-US), tone switching,
 * error handling, and clean light-theme styling.
 *
 * App.tsx は不変なので、URL hash (`#access_token=...`) を検知したときの OAuth
 * callback 処理もこのページの内部ステージとして保持する。
 *
 *   stage = "email"   → メールアドレス入力 + SSO
 *   stage = "sent"    → "メールを確認してください" success state
 *   stage = "verify"  → 6 桁コード入力 (VerifyPanel)
 *   stage = "oauth"   → OAuth callback hash の消費 (OAuthCallback)
 */

import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  KeyRound,
  Mail,
} from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useSession, setSession } from "@/lib/auth";
import { getTone } from "@/lib/i18n";
import { cn } from "@/lib/cn";
import {
  AuthApiError,
  createAuthApiClient,
  type AuthApiClient,
  type AuthSession,
  type AuthResponse,type MfaChallenge,isMfaChallenge,
  writeStoredSession,
} from "@/features/auth/api";
import { VerifyPanel } from "@/features/auth/VerifyPanel";
import { OAuthCallback } from "@/features/auth/OAuthCallback";
import {MfaPanel} from '@/features/auth/MfaPanel';
import {PasskeySignIn} from '@/features/auth/PasskeySignIn';
import { SsoCallback, SsoLoginForm, parseSsoCallback, readSsoPending } from '@/features/sso';
import {DESKTOP_PASSKEY_PARAM} from '@/features/auth/passkeys';

type Stage = "email" | "sent" | "verify" | "oauth" | 'mfa' | 'sso';

type LoginErrKey = "invalidEmail" | "rateLimited" | "network" | "unknown";

/** Test injection point — Vitest specs set window.__authClient before render. */
type WindowWithAuth = Window & { __authClient?: AuthApiClient };

export function LoginPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const tone = getTone();
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const loginParams = new URLSearchParams(location.search);
  const fromState = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  const invitationToken = loginParams.get("invitation") || (fromState?.pathname === "/auth/invitation" ? new URLSearchParams(fromState.search).get("token") : null);
  const oauthError = loginParams.get("error");

  const client = useMemo<AuthApiClient>(
    () => (window as WindowWithAuth).__authClient ?? createAuthApiClient(),
    [],
  );

  // initial stage: detect OAuth callback hash
  const initialHash =
    typeof window !== "undefined" && (window.location.hash.includes("access_token=")||window.location.hash.includes('mfa_required=true'))
      ? window.location.hash
      : "";
  const initialMfa=(location.state as {mfaChallenge?:unknown}|null)?.mfaChallenge;
  const [ssoSource] = useState(() => `${location.search}${location.hash || window.location.hash}`);
  const ssoCallback = parseSsoCallback(ssoSource);
  const [mfaChallenge,setMfaChallenge]=useState<MfaChallenge|null>(isMfaChallenge(initialMfa)?initialMfa:null);
  const [stage, setStage] = useState<Stage>(initialHash ? "oauth" : isMfaChallenge(initialMfa)?'mfa':ssoCallback?.kind === 'exchange' ? 'sso':"email");
  const [email, setEmail] = useState("");
  const [errorKey, setErrorKey] = useState<LoginErrKey | null>(null);
  const [pending, setPending] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const emailValid = isLikelyEmail(email);
  const showInlineEmailError = submitAttempted && !emailValid;

  const requestMagicLink = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setErrorKey(null);
    if (!emailValid) {
      setErrorKey("invalidEmail");
      return;
    }
    setPending(true);
    try {
      await client.requestMagicLink(email.trim());
      setStage("sent");
    } catch (err) {
      setErrorKey(mapLoginErr(err));
    } finally {
      setPending(false);
    }
  };

  const onVerified = useCallback((next: AuthResponse) => {
    if(isMfaChallenge(next)){setMfaChallenge(next);setStage('mfa');return;}
    writeStoredSession(next);
    // Mirror to lib/auth's session store so RequireAuth (uses useSession) sees it.
    setSession({
      user: {
        id: next.user.id,
        email: next.user.email,
        displayName:
          next.user.display_name || next.user.email.split("@")[0] || "user",
      },
      token: next.access_token,
    });
    const from = readSsoPending()?.kind === 'link' ? '/settings/profile' : invitationToken ? "/" : loginDestination(location.state);
    navigate(from ?? "/", { replace: true });
  }, [invitationToken, location.state, navigate]);
  const onSsoSession = useCallback((next: unknown) => onVerified(next as AuthResponse), [onVerified]);

  if (session && loginParams.get('reauth')!=='1' && !loginParams.get(DESKTOP_PASSKEY_PARAM) && !initialHash && !mfaChallenge && !ssoCallback) {
    const from = invitationToken ? "/" : loginDestination(location.state);
    return <Navigate to={from ?? "/"} replace />;
  }

  const onResend = async () => {
    if (!emailValid) return;
    try {
      await client.requestMagicLink(email.trim());
    } catch {
      // best-effort; user can retry
    }
  };

  const back = () => {
    setMfaChallenge(null);
    setStage("email");
    setErrorKey(null);
    setSubmitAttempted(false);
  };

  const onLanguageChange = (lng: string) => {
    void i18n.changeLanguage(lng);
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("ailechat:lang", lng);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface-base">
      <a
        href="#login-main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-canvas px-3 py-1 rounded shadow-md text-sm"
      >
        {t("nav.skipToContent", { defaultValue: "Skip to content" })}
      </a>

      <main
        id="login-main"
        className="flex-1 flex items-center justify-center px-4 py-10 sm:py-16"
      >
        <div className="w-full max-w-md">
          <BrandHeader />

          <section
            aria-labelledby="login-heading"
            className="bg-canvas rounded-lg shadow-md border border-border-subtle p-6 sm:p-8"
          >
            {loginParams.get('account_deleted')==='1'&&<p role="status" className="mb-4 text-sm">アカウントを削除しました。</p>}
            {oauthError && <p role="alert" className="text-danger mb-3">ログインできませんでした: {oauthError.replace(/_/g, " ")}</p>}
            {ssoCallback && stage !== 'mfa' && <SsoCallback search={ssoSource} token={session?.token} onSession={onSsoSession}
              onNeedLink={() => { if (session) navigate('/settings/profile'); else setStage('email'); }} />}
            {stage==='mfa'&&mfaChallenge&&<MfaPanel challenge={mfaChallenge} client={client} onVerified={onVerified} onBack={back}/>}
            {stage === "email" ? (
              <EmailStage
                tone={tone}
                email={email}
                setEmail={(v) => {
                  setEmail(v);
                  if (errorKey) setErrorKey(null);
                }}
                onSubmit={requestMagicLink}
                pending={pending}
                errorKey={errorKey}
                showInlineEmailError={showInlineEmailError}
                client={invitationToken ? { ...client, googleStartUrl: () => client.googleStartUrl(invitationToken), microsoftStartUrl: () => client.microsoftStartUrl(invitationToken) } : client}
              />
            ) : null}

            {stage==='email'&&<PasskeySignIn onVerified={onVerified} disabled={pending}/>}

            {stage === "sent" ? (
              <SentStage
                email={email}
                onBack={back}
                onResend={onResend}
                onContinueToVerify={() => setStage("verify")}
              />
            ) : null}

            {stage === "verify" ? (
              <VerifyPanel
                email={email}
                client={client}
                invitationToken={invitationToken || undefined}
                onVerified={onVerified}
                onBack={back}
                onResend={onResend}
              />
            ) : null}

            {stage === "oauth" ? (
              <OAuthCallback
                hash={initialHash}
                onSuccess={onVerified}
                onBack={() => {
                  setStage("email");
                  if (typeof window !== "undefined") {
                    window.history.replaceState(null, "", "/login");
                  }
                }}
              />
            ) : null}
          </section>

          <Footer
            currentLanguage={i18n.language}
            onLanguageChange={onLanguageChange}
          />
        </div>
      </main>
    </div>
  );
}

// =============================================================================
// Email stage (Magic-link request + SSO buttons)
// =============================================================================

interface EmailStageProps {
  tone: ReturnType<typeof getTone>;
  email: string;
  setEmail: (v: string) => void;
  onSubmit: (e: FormEvent) => void | Promise<void>;
  pending: boolean;
  errorKey: LoginErrKey | null;
  showInlineEmailError: boolean;
  client: AuthApiClient;
}

function EmailStage({
  tone,
  email,
  setEmail,
  onSubmit,
  pending,
  errorKey,
  showInlineEmailError,
  client,
}: EmailStageProps): JSX.Element {
  const { t } = useTranslation();
  const [methods,setMethods]=useState<{google:boolean;microsoft:boolean;saml:boolean}|null>(client.signInMethods?null:{google:true,microsoft:true,saml:true});
  useEffect(()=>{let active=true;if(client.signInMethods)void client.signInMethods().then(value=>{if(active)setMethods(value);}).catch(()=>{if(active)setMethods(null);});return()=>{active=false;};},[client]);
  return (
    <div>
      <h1 id="login-heading" className="text-xl font-bold text-fg-primary mb-1">
        {t("auth.login.title")}
      </h1>
      <p className="text-sm text-fg-secondary mb-5 leading-relaxed">
        {t("auth.login.subtitle")}
      </p>

      <form onSubmit={onSubmit} aria-busy={pending || undefined} noValidate>
        <label
          className="block text-sm font-medium text-fg-primary mb-1.5"
          htmlFor="login-email"
        >
          {t("auth.login.email")}
        </label>
        <Input
          id="login-email"
          type="email"
          autoComplete="email"
          inputMode="email"
          autoFocus
          required
          value={email}
          invalid={showInlineEmailError || errorKey === "invalidEmail"}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("auth.login.emailPlaceholder") ?? ""}
          aria-describedby="login-email-help login-email-error"
          aria-invalid={
            showInlineEmailError || errorKey === "invalidEmail" ? true : undefined
          }
          disabled={pending}
        />
        <div
          id="login-email-error"
          role="alert"
          aria-live="polite"
          className="min-h-[1.25rem] mt-1.5"
        >
          {errorKey ? (
            <p className="text-sm text-danger inline-flex items-center gap-1">
              <AlertCircle size={14} aria-hidden />
              <span>{t(`auth.login.errors.${errorKey}`)}</span>
            </p>
          ) : null}
        </div>

        <Button
          type="submit"
          className="w-full mt-1"
          loading={pending}
          disabled={pending}
          leadingIcon={<Mail size={16} aria-hidden />}
        >
          {pending
            ? t("auth.login.sending")
            : (t(`auth.login.submit.${tone}`, {
                defaultValue: t("auth.login.submit.polite"),
              }) as string)}
        </Button>
      </form>

      {(methods?.google||methods?.microsoft||methods?.saml)&&<><Divider label={t("auth.login.or")} />
      <div className="space-y-2">
        {methods?.google&&<SsoButton
          href={client.googleStartUrl()}
          label={t("auth.login.ssoGoogle")}
          icon={<GoogleMark />}
        />}
        {methods?.microsoft&&<SsoButton
          href={client.microsoftStartUrl()}
          label={t("auth.login.ssoMicrosoft")}
          icon={<MicrosoftMark />}
        />}
            {methods?.saml&&<SsoSamlButton />}
      </div></>}
    </div>
  );
}

// =============================================================================
// Sent stage (success state — "check your email")
// =============================================================================

interface SentStageProps {
  email: string;
  onBack: () => void;
  onResend: () => void | Promise<void>;
  onContinueToVerify: () => void;
}

function SentStage({
  email,
  onBack,
  onResend,
  onContinueToVerify,
}: SentStageProps): JSX.Element {
  const { t } = useTranslation();
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt] = useState<number | null>(null);

  const resend = async () => {
    setResending(true);
    try {
      await onResend();
      setResentAt(Date.now());
    } finally {
      setResending(false);
    }
  };

  return (
    <div role="status" aria-live="polite">
      <div className="flex items-start gap-3 mb-3">
        <div className="shrink-0 w-10 h-10 rounded-full bg-success-bg flex items-center justify-center">
          <CheckCircle2 size={20} className="text-success" aria-hidden />
        </div>
        <div className="flex-1 pt-1">
          <h2 className="text-lg font-bold text-fg-primary mb-1">
            {t("auth.login.sentTitle")}
          </h2>
          <p className="text-sm text-fg-secondary leading-relaxed">
            {t("auth.login.sentDescription", { email })}
          </p>
        </div>
      </div>

      <Button
        type="button"
        className="w-full mt-2"
        onClick={onContinueToVerify}
        leadingIcon={<KeyRound size={16} aria-hidden />}
      >
        {t("auth.verify.codeLabel")}
      </Button>

      <div className="mt-4 pt-4 border-t border-border-subtle text-sm">
        <p className="text-fg-secondary mb-2">{t("auth.login.sentNotArrived")}</p>
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-fg-secondary hover:text-fg-primary focus-visible:shadow-focus rounded px-1 py-0.5"
          >
            <ArrowLeft size={14} aria-hidden /> {t("auth.login.sentResend")}
          </button>
          <button
            type="button"
            onClick={resend}
            disabled={resending}
            className={cn(
              "text-brand-700 hover:underline focus-visible:shadow-focus rounded px-1 py-0.5",
              resending && "opacity-60 cursor-not-allowed",
            )}
          >
            {resending ? t("auth.login.sending") : t("auth.login.sentResendButton")}
          </button>
        </div>
        {resentAt ? (
          <p className="text-xs text-fg-muted mt-2">
            {t("auth.login.sent")}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// =============================================================================
// Bits & pieces
// =============================================================================

function BrandHeader(): JSX.Element {
  const { t } = useTranslation();
  return (
    <header className="mb-6 flex items-center gap-3 justify-center">
      <BridgeMark />
      <div>
        <p className="text-lg font-bold text-fg-primary leading-tight">
          {t("app.name")}{" "}
          <span className="text-fg-muted text-sm font-medium">{t("app.name_ja")}</span>
        </p>
        <p className="text-xs text-fg-muted">{t("app.tagline")}</p>
      </div>
    </header>
  );
}

function Divider({ label }: { label: string }): JSX.Element {
  return (
    <div className="my-5 flex items-center gap-3" aria-hidden>
      <div className="flex-1 h-px bg-border-subtle" />
      <span className="text-xs text-fg-muted uppercase tracking-wider">{label}</span>
      <div className="flex-1 h-px bg-border-subtle" />
    </div>
  );
}

interface SsoButtonProps {
  href: string;
  label: string;
  icon: JSX.Element;
}

function SsoButton({ href, label, icon }: SsoButtonProps): JSX.Element {
  return (
    <a
      href={href}
      className={cn(
        "w-full inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md",
        "bg-canvas text-fg-primary border border-border-default",
        "hover:bg-surface-base active:bg-surface-raised",
        "text-base font-medium transition-colors duration-150",
        "focus-visible:shadow-focus",
      )}
    >
      <span className="inline-flex shrink-0">{icon}</span>
      <span>{label}</span>
    </a>
  );
}

function SsoSamlButton(): JSX.Element {
  const { t } = useTranslation();
  return (
    <details className="group rounded-md border border-border-subtle bg-surface-base">
      <summary
        className={cn(
          "flex items-center justify-between gap-2 h-10 px-4 cursor-pointer rounded-md",
          "text-base font-medium text-fg-primary",
          "hover:bg-surface-raised focus-visible:shadow-focus list-none",
        )}
      >
        <span className="inline-flex items-center gap-2">
          <KeyRound size={16} aria-hidden className="text-brand-700" />
          {t("auth.login.ssoSaml")}
        </span>
        <span className="text-xs text-fg-muted font-normal">
          組織のアカウント
        </span>
      </summary>
      <div className="px-4 pb-3 pt-1 text-sm text-fg-secondary">
        <SsoLoginForm />
      </div>
    </details>
  );
}

function Footer({
  currentLanguage,
  onLanguageChange,
}: {
  currentLanguage: string;
  onLanguageChange: (lng: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  // i18next gives "ja-JP", "en-US", or short codes "ja"/"en"; normalize to long.
  const normalized = currentLanguage.startsWith("en") ? "en-US" : "ja-JP";
  return (
    <footer className="mt-6 text-center text-xs text-fg-muted">
      <nav aria-label={t("common.details")} className="flex items-center justify-center gap-3 mb-2 flex-wrap">
        <a
          href="/legal/terms"
          className="hover:text-fg-secondary focus-visible:shadow-focus rounded px-1"
        >
          {t("auth.login.terms")}
        </a>
        <span aria-hidden>·</span>
        <a
          href="/legal/privacy"
          className="hover:text-fg-secondary focus-visible:shadow-focus rounded px-1"
        >
          {t("auth.login.privacy")}
        </a>
        <span aria-hidden>·</span>
        <a
          href="https://chat-lp.aile-ai.jp/#faq"
          className="hover:text-fg-secondary focus-visible:shadow-focus rounded px-1"
        >
          {t("auth.login.support")}
        </a>
      </nav>
      <div className="inline-flex items-center gap-2">
        <label htmlFor="login-lang" className="sr-only">
          {t("auth.login.language")}
        </label>
        <select
          id="login-lang"
          value={normalized}
          onChange={(e) => onLanguageChange(e.target.value)}
          className={cn(
            "h-7 px-2 rounded border border-border-subtle bg-canvas text-xs",
            "text-fg-secondary hover:text-fg-primary hover:border-border-default",
            "focus-visible:shadow-focus",
          )}
        >
          <option value="ja-JP">{t("auth.login.languages.ja-JP")}</option>
          <option value="en-US">{t("auth.login.languages.en-US")}</option>
        </select>
      </div>
      <p className="mt-2 text-xs text-fg-muted leading-relaxed">
        {t("auth.login.termsPrefix")}{" "}
        <a href="/legal/terms" className="underline hover:text-fg-secondary">
          {t("auth.login.terms")}
        </a>{" "}
        {t("auth.login.and")}{" "}
        <a href="/legal/privacy" className="underline hover:text-fg-secondary">
          {t("auth.login.privacy")}
        </a>
        {t("auth.login.termsSuffix")}
      </p>
    </footer>
  );
}

// =============================================================================
// Marks
// =============================================================================

function BridgeMark(): JSX.Element {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-brand-700"
      aria-hidden="true"
    >
      <path d="M2.5 11 V14.25 Q2.5 16.5 4.75 16.5 H6 V19 L9.25 16.5 H17.25 Q19.5 16.5 19.5 14.25 V11 Z" />
      <path d="M6 11 V3 M16 11 V3" />
      <path d="M2.5 11 L6 4.75 Q11 9.25 16 4.75 L19.5 11" />
    </svg>
  );
}

function GoogleMark(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M9 3.48c1.69 0 2.84.73 3.49 1.34l2.55-2.49C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.97 2.31C4.62 5.27 6.65 3.48 9 3.48z"
      />
      <path
        fill="#34A853"
        d="M17.64 9.2c0-.74-.06-1.28-.2-1.84H9v3.34h4.96c-.1.83-.64 2.08-1.83 2.92l2.86 2.22C16.69 14.33 17.64 11.99 17.64 9.2z"
      />
      <path
        fill="#FBBC05"
        d="M3.93 10.73a5.41 5.41 0 0 1-.29-1.73c0-.6.11-1.18.28-1.73L.96 4.96A8.95 8.95 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.97-2.31z"
      />
      <path
        fill="#4285F4"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.86-2.22c-.78.54-1.79.92-3.1.92-2.35 0-4.38-1.79-5.07-4.27L.96 13.04C2.44 15.98 5.48 18 9 18z"
      />
    </svg>
  );
}

function MicrosoftMark(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#F25022" d="M0 0h8.5v8.5H0z" />
      <path fill="#7FBA00" d="M9.5 0H18v8.5H9.5z" />
      <path fill="#00A4EF" d="M0 9.5h8.5V18H0z" />
      <path fill="#FFB900" d="M9.5 9.5H18V18H9.5z" />
    </svg>
  );
}

// =============================================================================
// Helpers
// =============================================================================

/** Conservative RFC 5321-ish email check — backend is the source of truth. */
function isLikelyEmail(s: string): boolean {
  const trimmed = s.trim();
  if (!trimmed) return false;
  if (trimmed.length > 254) return false;
  // local@domain.tld minimum
  const m = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  return m;
}

function mapLoginErr(e: unknown): LoginErrKey {
  if (e instanceof AuthApiError) {
    if (e.status === 429 || e.code === "RATE_LIMITED") return "rateLimited";
    if (e.status === 400 || e.code === "INVALID_EMAIL") return "invalidEmail";
    if (e.status === 0 || e.code.startsWith("NETWORK")) return "network";
    return "unknown";
  }
  if (e instanceof TypeError && /fetch|network/i.test(e.message)) return "network";
  return "unknown";
}

export function loginDestination(state: unknown): string | undefined {
  const from = (state as { from?: { pathname?: string; search?: string } } | null)?.from;
  if (typeof from?.pathname !== "string" || !from.pathname.startsWith("/") || from.pathname.startsWith("//") || /[\\\u0000-\u001f\u007f]/.test(from.pathname)) return undefined;
  const search = from.search ?? "";
  if (typeof search !== "string" || (search && !search.startsWith("?")) || /[\u0000-\u001f\u007f]/.test(search)) return undefined;
  return `${from.pathname}${search}`;
}
